import JsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Motor genérico de exportación a PDF
 * Usa una Strategy para obtener la configuración específica de cada reporte
 */
export default class PDFExporter {
  constructor(strategy, data, context) {
    this.strategy = strategy;
    this.data = data;
    this.context = context;

    // Configuración de página
    this.marginLeft = 8;
    this.marginRight = 8;
    this.marginTop = 12;
    this.marginBottom = 12;
  }

  /**
   * Genera y descarga el PDF
   */
  generate() {
    // Obtener columnas para determinar el tamaño de papel necesario
    const columns = this.strategy.getVisibleColumns({
      reportType: this.context.esOperacion,
      includeSubtotals: true,
      maxDesembolsos: this.context.maxDesembolsos,
      columnasMeses: this.context.columnasMeses,
      columnasPeriodos: this.context.columnasPeriodos,
      datos: this.data
    });

    // Permitir que la estrategia sobrescriba la configuración del PDF
    let pdfConfig;
    if (this.strategy.getPreferredPDFConfig) {
      pdfConfig = this.strategy.getPreferredPDFConfig(columns.length);
    } else {
      // Determinar configuración basada en cantidad de columnas
      pdfConfig = this.getPDFConfiguration(columns.length);
    }

    // Mostrar advertencia si hay muchas columnas
    if (pdfConfig.advertenciaUsuario) {
      console.warn(`⚠️ ADVERTENCIA: El reporte tiene ${columns.length} columnas. Para mejor visualización, se recomienda usar la exportación a Excel.`);
    }

    // Crear documento con el tamaño apropiado
    const doc = this.createDocument(pdfConfig.usarA3, pdfConfig.customSize);

    // Guardar configuración para uso posterior
    this.pdfConfig = pdfConfig;
    this.columns = columns;

    // Agregar metadatos (título, subtítulo, fechas)
    const yAfterMetadata = this.addMetadata(doc);

    // Agregar tabla con los datos
    this.addTable(doc, yAfterMetadata);

    // Descargar
    doc.save(this.strategy.getFileName('pdf'));
  }

  /**
   * Crea el documento PDF con orientación landscape
   * @param {Boolean} usarA3 - Si true, usa papel A3, sino A4
   * @param {String|Array} customSize - Tamaño personalizado (ej: 'a2' o [700, 500]) si es necesario
   */
  createDocument(usarA3 = false, customSize = null) {
    let pageSize = 'a4';
    let sizeLabel = 'A4';

    if (customSize) {
      pageSize = customSize;
      // Si es un array, es un tamaño personalizado
      if (Array.isArray(customSize)) {
        sizeLabel = `CUSTOM (${customSize[0]}x${customSize[1]}mm)`;
      } else {
        sizeLabel = customSize.toUpperCase();
      }
    } else if (usarA3) {
      pageSize = 'a3';
      sizeLabel = 'A3';
    }

    console.log(`📄 PDF Export: Usando papel ${sizeLabel} landscape`);
    return new JsPDF('l', 'mm', pageSize);
  }

  /**
   * Agrega los metadatos (título, subtítulo, fechas) al documento
   * @param {Object} doc - Documento jsPDF
   * @returns {Number} - Posición Y después de los metadatos
   */
  addMetadata(doc) {
    const metadata = this.strategy.getMetadata(this.data, this.context);

    let currentY = this.marginTop;

    // Título principal
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(metadata.title, this.marginLeft, currentY);
    currentY += 6;

    // Subtítulo (fideicomisos)
    if (metadata.subtitle) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(metadata.subtitle, this.marginLeft, currentY);
      currentY += 5;
    }

    // Operatorias/Líneas (puede tener múltiples líneas)
    if (metadata.operatorias) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Dividir por saltos de línea y mostrar cada línea
      const lineasOperatorias = metadata.operatorias.split('\n');
      lineasOperatorias.forEach((linea, index) => {
        doc.text(linea, this.marginLeft, currentY);
        currentY += 5; // Espaciado entre líneas
      });

      currentY += 1; // Espacio adicional después del bloque de operatorias
    }

    // Periodo (solo para reportes que lo incluyan, como HTC)
    if (metadata.periodo) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Periodo: ${metadata.periodo}`, this.marginLeft, currentY);
      currentY += 6;
    }

    // Fecha de consulta
    doc.setFontSize(8);
    doc.text(`Fecha de consulta: ${metadata.fechaConsulta}`, this.marginLeft, currentY);
    currentY += 5;

    // Fecha de generación
    doc.text(`Generado el: ${metadata.fechaGeneracion}`, this.marginLeft, currentY);
    currentY += 10; // Espacio antes de la tabla

    return currentY;
  }

  /**
   * Agrega la tabla de datos al documento
   * @param {Object} doc - Documento jsPDF
   * @param {Number} startY - Posición Y donde empieza la tabla
   */
  addTable(doc, startY) {
    // Usar columnas y configuración ya calculadas en generate()
    const columns = this.columns;
    const pdfConfig = this.pdfConfig;

    const tableData = this.transformData(columns);
    const columnConfig = this.createColumnConfig(columns, doc, pdfConfig);

    doc.autoTable({
      head: [columns.map(col => col.header)],
      body: tableData.rows,
      startY: startY,

      // Estilos generales
      styles: {
        fontSize: pdfConfig.fontSize,
        cellPadding: pdfConfig.cellPadding,
        overflow: 'linebreak',
        halign: 'left',
        minCellHeight: pdfConfig.minCellHeight,
        cellWidth: 'wrap',
      },

      // Estilos del header
      headStyles: {
        fillColor: [148, 159, 175],
        textColor: [255, 255, 255],
        fontSize: pdfConfig.fontSize,
        fontStyle: 'bold',
        halign: 'center',
        minCellHeight: pdfConfig.minCellHeight + 2,
      },

      // Configuración de columnas
      columnStyles: columnConfig,

      // Filas alternadas
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },

      // Aplicar estilos a filas especiales (subtotales, totales)
      didParseCell: (data) => {
        this.applyRowStyles(data, tableData.rowMetadata[data.row.index], pdfConfig);
      },

      // Configuración de la tabla
      tableWidth: 'wrap',
      margin: {
        left: this.marginLeft,
        right: this.marginRight,
        top: this.marginTop,
        bottom: this.marginBottom
      },
      theme: 'grid',
      showHead: 'everyPage',

      // Números de página
      didDrawPage: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.text(
          `Página ${data.pageNumber}`,
          pageWidth - this.marginRight - 15,
          pageHeight - 8
        );
      }
    });
  }

  /**
   * Transforma los datos para la tabla del PDF
   * @param {Array} columns
   * @returns {Object} { rows, rowMetadata }
   */
  transformData(columns) {
    const displayData = this.strategy.getDisplayData(this.data);
    const regularData = this.strategy.getRegularData(this.data);

    const rows = [];
    const rowMetadata = [];

    // Agregar filas regulares y subtotales
    displayData.forEach(item => {
      const row = columns.map(col => this.transformCell(item, col));
      rows.push(row);

      rowMetadata.push({
        isSubtotal: item._esSubtotal || false,
        isTotal: false
      });
    });

    // Agregar fila de totales
    const totalRow = columns.map(col => this.transformTotalCell(regularData, col));
    rows.push(totalRow);
    rowMetadata.push({
      isSubtotal: false,
      isTotal: true
    });

    return { rows, rowMetadata };
  }

  /**
   * Transforma una celda individual
   * @param {Object} item
   * @param {Object} column
   * @returns {String}
   */
  transformCell(item, column) {
    let result;

    if (item._esSubtotal && column.formatSubtotal) {
      result = column.formatSubtotal(item, this.context, this.data);
    } else {
      result = column.format(item, this.context);
    }

    return this.strategy.getDisplayValue(result);
  }

  /**
   * Transforma una celda de la fila de totales
   * @param {Array} regularData
   * @param {Object} column
   * @returns {String}
   */
  transformTotalCell(regularData, column) {
    let result;

    if (column.formatTotal) {
      result = column.formatTotal(regularData, this.context);
    } else if (column.calculateTotal) {
      const totalValue = column.calculateTotal(regularData, this.context);
      result = this.strategy.formatMonto(totalValue);
    } else {
      result = '';
    }

    return this.strategy.getDisplayValue(result);
  }

  /**
   * Crea la configuración de columnas para jsPDF autotable
   * @param {Array} columns
   * @param {Object} doc - Documento jsPDF
   * @param {Object} pdfConfig - Configuración del PDF
   * @returns {Object}
   */
  createColumnConfig(columns, doc, pdfConfig) {
    const config = {};

    // Obtener ancho real de la página (A4 o A3)
    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - this.marginLeft - this.marginRight;

    console.log(`📊 PDF: Ancho de página = ${pageWidth}mm, ancho utilizable = ${usableWidth}mm`);

    columns.forEach((col, index) => {
      const width = col.pdfWidth
        ? col.pdfWidth * pdfConfig.factorReduccion
        : Math.max(10, usableWidth * 0.02 * pdfConfig.factorReduccion);

      config[index] = {
        cellWidth: width,
        halign: col.align || 'left'
      };
    });

    return config;
  }

  /**
   * Obtiene la configuración óptima del PDF según cantidad de columnas
   * @param {Number} totalColumns
   * @returns {Object}
   */
  getPDFConfiguration(totalColumns) {
    const strategies = {
      extremo: totalColumns > 40,        // Más de 40 columnas: Usar ancho custom extra grande
      a2Necesario: totalColumns > 30,    // Más de 30 columnas: Usar A2
      a3Necesario: totalColumns > 15,    // Más de 15 columnas: Usar A3
      ultraCompacta: totalColumns > 25,
      compacta: totalColumns > 20 && totalColumns <= 25,
      normal: totalColumns <= 20 && totalColumns > 10,
      amplia: totalColumns <= 10
    };

    let description, customSize, advertenciaUsuario;

    if (strategies.extremo) {
      // Más de 40 columnas: usar ancho personalizado extra grande (700mm)
      description = `⚠️ Configuración extrema (${totalColumns} columnas) - Ancho personalizado 700mm`;
      customSize = [700, 500]; // [ancho, alto] en mm - Extra ancho para 40+ columnas
      advertenciaUsuario = true;
    } else if (strategies.a2Necesario) {
      // 31-40 columnas: usar A2 (594mm de ancho)
      description = `Configuración A2 landscape (${totalColumns} columnas)`;
      customSize = 'a2';
      advertenciaUsuario = true;
    } else if (strategies.a3Necesario) {
      // 16-30 columnas: usar A3 (420mm de ancho)
      description = `Configuración A3 landscape (${totalColumns} columnas)`;
      customSize = null;
    } else {
      description = strategies.compacta ? 'Configuración compacta (21-25 columnas)' :
                   strategies.normal ? 'Configuración normal (11-20 columnas)' :
                   'Configuración amplia (hasta 10 columnas)';
      customSize = null;
      advertenciaUsuario = false;
    }

    console.log(`📄 PDF Export: ${totalColumns} columnas detectadas - ${description}`);

    return {
      usarPortrait: false, // Forzar landscape
      usarA3: strategies.a3Necesario && !strategies.a2Necesario && !strategies.extremo,
      customSize: customSize,
      advertenciaUsuario: advertenciaUsuario,
      // Reducción más agresiva para ultraCompacta (26-30 cols): 0.65 → 0.56
      factorReduccion: strategies.extremo ? 0.55 : strategies.ultraCompacta ? 0.56 : strategies.compacta ? 0.70 : strategies.normal ? 0.85 : 1.0,
      fontSize: strategies.extremo ? 4.5 : strategies.ultraCompacta ? 5 : strategies.compacta ? 5.5 : strategies.normal ? 6 : 7,
      cellPadding: strategies.extremo ? 0.8 : strategies.ultraCompacta ? 1 : strategies.compacta ? 1.2 : strategies.normal ? 1.5 : 2,
      minCellHeight: strategies.extremo ? 5 : strategies.ultraCompacta ? 5.5 : strategies.compacta ? 6 : strategies.normal ? 6.5 : 7,
    };
  }

  /**
   * Aplica estilos a filas especiales (subtotales, totales)
   * @param {Object} data - Data de jsPDF autotable didParseCell
   * @param {Object} metadata - Metadata de la fila
   * @param {Object} pdfConfig - Configuración del PDF
   */
  applyRowStyles(data, metadata, pdfConfig) {
    if (!metadata) return;

    const styles = this.strategy.getRowStyles();

    if (metadata.isTotal && styles.total) {
      Object.assign(data.cell.styles, styles.total.pdf);
      // Reducir el tamaño de letra en la fila de totales (15% más pequeño)
      const baseFontSize = pdfConfig.fontSize;
      data.cell.styles.fontSize = baseFontSize * 0.85;
    } else if (metadata.isSubtotal && styles.subtotal) {
      Object.assign(data.cell.styles, styles.subtotal.pdf);
    }
  }
}
